// The feedback button, in the editor only (/edit/): the todos board's widget
// (doing.stuff.lazymath.xyz/feedback.js), which makes a task on the board from what is typed,
// with the page's address. Loaded only once the editor's server has answered (the scenes list,
// or a scene's scans), so the public viewer never fetches it.
//
// What goes with the sentence, unseen: the widget's hidden fields, read at each send, so they
// are the state when it was written: the build, the scene, the scans and their history heads,
// the selection and the tool, the camera and the GPU.

const SRC = 'https://doing.stuff.lazymath.xyz/feedback.js';
/** The board's key for cloudview (made in the repo's settings on the board). It is not a
 *  secret: all it can do is make a task in its team and repo. */
const KEY = '__FEEDBACK_KEY__';

export type Fields = Record<string, string | number | boolean | null>;
type Widget = { mount?: (o: object) => void };

let mounted = false;
let extra: () => Fields = () => ({});

/** Load and mount the button (once); `fields` adds to what is sent from here on. */
export function editorFeedback(fields?: () => Fields) {
  if (fields) extra = fields;
  if (mounted || !/^[0-9a-f]{32}$/.test(KEY)) return;
  mounted = true;
  const s = document.createElement('script');
  s.src = SRC; s.async = true;
  s.onload = () => (window as unknown as { TodosFeedback?: Widget }).TodosFeedback?.mount?.({
    key: KEY, corner: 'bottom-right', compact: true, fields: collect,
  });
  s.onerror = () => { mounted = false; console.warn('feedback: the board\'s widget did not load'); };
  document.head.append(s);
}

/** The build: app.js's ?v= hash (build.mjs). */
const build = () => {
  const src = document.querySelector<HTMLScriptElement>('script[src*="app.js"]')?.src;
  return src ? new URL(src).searchParams.get('v') : null;
};

let gpu: string | null | undefined;
function renderer() {
  if (gpu !== undefined) return gpu;
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    const dbg = gl?.getExtension('WEBGL_debug_renderer_info');
    gpu = gl ? String(gl.getParameter(dbg ? dbg.UNMASKED_RENDERER_WEBGL : gl.RENDERER)) : null;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch { gpu = null; }
  return gpu;
}

function collect(): Fields {
  const out: Fields = { build: build(), gpu: renderer(), screen: `${innerWidth}x${innerHeight}@${devicePixelRatio}` };
  try { Object.assign(out, extra()); } catch (e) { out.fieldsError = String((e as Error)?.message || e); }
  return out;
}
